/**
 * Site feature flags — pure helpers shared by every consumer.
 *
 * The backend registry (`apps/core/features/registry.py`) is the source of
 * truth. `routes.ts` is generated from it: every route carries either
 * `feature: "<key>"` or `core: true`, each feature's home route carries a
 * `landingPriority`, and `FEATURE_ROUTE_PREFIXES` maps static path prefixes to
 * their feature for consumers that only hold an href.
 *
 * Shared code must not import the app's generated `routes.ts`, so the app
 * registers it once at boot with {@link configureFeatureRoutes} (and the test
 * setup does the same). Everything here is O(1) or a single pass over a small
 * table; nothing allocates per render beyond what the caller memoizes.
 *
 * Fail closed: a feature is on only when the flag map says exactly `true`.
 * Routes that declare no `feature` (core, or hand-built fixtures) are always
 * visible — flags gate features, never core surfaces.
 *
 * @module _shared_frontend/lib/site-features
 */

export type SiteFeatureFlags = Readonly<Record<string, boolean>>;

export interface SiteFeatureFlagsPayload {
  featureFlags: SiteFeatureFlags;
  version: number;
}

export interface FeatureGatedRoute {
  path: string;
  name?: string;
  feature?: string;
  core?: boolean;
  landingPriority?: number;
  authRequired?: boolean;
  layout?: string;
}

export type FeatureRoutePrefixTable = ReadonlyArray<readonly [string, string | null]>;

interface FeatureRouteConfig {
  prefixes: FeatureRoutePrefixTable;
  routes: readonly FeatureGatedRoute[];
}

let config: FeatureRouteConfig = { prefixes: [], routes: [] };

/** Register the generated route table (call once at boot, and in test setup). */
export function configureFeatureRoutes(next: FeatureRouteConfig): void {
  config = {
    // Keep the table longest-first even if a caller hands it unsorted.
    prefixes: [...next.prefixes].sort((a, b) => b[0].length - a[0].length),
    routes: next.routes,
  };
}

export function getConfiguredFeatureRoutes(): readonly FeatureGatedRoute[] {
  return config.routes;
}

/**
 * `true` only when the map says exactly `true` for the key **and** every
 * dotted ancestor (`diet` for `diet.food_log`). The server already sends
 * effective values; re-checking ancestors keeps a hand-built or partially
 * merged map from ever showing a child under a disabled parent.
 */
export function isFeatureOn(
  flags: SiteFeatureFlags | null | undefined,
  key: string,
): boolean {
  if (!flags || flags[key] !== true) return false;
  for (let dot = key.indexOf("."); dot !== -1; dot = key.indexOf(".", dot + 1)) {
    if (flags[key.slice(0, dot)] !== true) return false;
  }
  return true;
}

/** Routes without a `feature` are core (or fixtures) and always pass. */
export function isRouteFeatureEnabled(
  route: Pick<FeatureGatedRoute, "feature">,
  flags: SiteFeatureFlags | null | undefined,
): boolean {
  return !route.feature || isFeatureOn(flags, route.feature);
}

export function filterRoutesByFeatures<T extends Pick<FeatureGatedRoute, "feature">>(
  routes: readonly T[],
  flags: SiteFeatureFlags | null | undefined,
): T[] {
  return routes.filter((route) => isRouteFeatureEnabled(route, flags));
}

const LANG_PREFIX = /^\/(?:en|ar|de|es|fr)(?=\/|$)/;

function stripLang(pathname: string): string {
  if (pathname === "/:lang") return "/";
  if (pathname.startsWith("/:lang/")) return pathname.slice("/:lang".length);
  return pathname.replace(LANG_PREFIX, "") || "/";
}

function pathOnly(href: string): string {
  const cut = href.search(/[?#]/);
  return cut === -1 ? href : href.slice(0, cut);
}

/**
 * Feature owning `href` by longest static prefix: a key, `null` for a core
 * path, or `undefined` when the path is not in the generated table (landing,
 * legal pages, docs, external links).
 */
export function featureForPath(href: string): string | null | undefined {
  const path = stripLang(pathOnly(href));
  for (const [prefix, feature] of config.prefixes) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return feature;
  }
  return undefined;
}

/** Whether `href` may be shown/linked: unknown and core paths always may. */
export function isPathFeatureEnabled(
  href: string,
  flags: SiteFeatureFlags | null | undefined,
): boolean {
  const feature = featureForPath(href);
  return feature == null || isFeatureOn(flags, feature);
}

/** First candidate href whose feature is enabled (for CTAs), else `undefined`. */
export function firstEnabledPath(
  candidates: readonly string[],
  flags: SiteFeatureFlags | null | undefined,
): string | undefined {
  return candidates.find((href) => isPathFeatureEnabled(href, flags));
}

export const LANDING_FALLBACK_PATH = "/profile";

/**
 * Post-login / fallback destination: the enabled feature home route with the
 * lowest `landingPriority`. `isAllowed` lets callers add the paywall/BYOK
 * layer (a flagged-on AI page may still not exist on a free deployment).
 * Never returns a disabled page; falls back to a core page.
 */
export function resolveLandingPath(
  flags: SiteFeatureFlags | null | undefined,
  isAllowed: (route: FeatureGatedRoute) => boolean = () => true,
): string {
  let best: FeatureGatedRoute | undefined;
  for (const route of config.routes) {
    if (route.landingPriority === undefined || route.path.startsWith("/:lang")) continue;
    if (!route.feature || !isFeatureOn(flags, route.feature)) continue;
    if (!isAllowed(route)) continue;
    if (!best || route.landingPriority < (best.landingPriority ?? Infinity)) best = route;
  }
  return best?.path ?? LANDING_FALLBACK_PATH;
}

/** Normalize the SSR bootstrap, accepting the legacy flat-map shape. */
export function normalizeFeatureFlagsPayload(
  raw: unknown,
): SiteFeatureFlagsPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as Partial<SiteFeatureFlagsPayload> & Record<string, unknown>;
  const map =
    candidate.featureFlags && typeof candidate.featureFlags === "object"
      ? candidate.featureFlags
      : (candidate as Record<string, unknown>);
  const featureFlags: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(map)) {
    if (typeof value === "boolean") featureFlags[key] = value;
  }
  return {
    featureFlags,
    version: typeof candidate.version === "number" ? candidate.version : -1,
  };
}
